import { cx } from "@/lib/cx.js";

/*
 * The .card / .card-head / .card-title / .card-extra / .card-body classes come
 * from index.css. These are thin wrappers so pages read as structure rather than
 * as class strings, and so a change to the shape happens in one place.
 *
 * .card-head and .card-extra both wrap by default — a head with several controls
 * has to stack on a phone rather than push the page into a horizontal scroll.
 */

export function Card({ className, children, ...rest }) {
    return (
        <section className={cx("card", className)} {...rest}>
            {children}
        </section>
    );
}

export function CardHead({ className, children, ...rest }) {
    return (
        <div className={cx("card-head", className)} {...rest}>
            {children}
        </div>
    );
}

export function CardTitle({ as: Tag = "h2", icon, hint, className, children, ...rest }) {
    return (
        <Tag className={cx("card-title", className)} {...rest}>
            {icon}
            {children}
            {hint && (
                <span className="text-xs font-normal text-slate-400 dark:text-slate-500">{hint}</span>
            )}
        </Tag>
    );
}

export function CardExtra({ className, children, ...rest }) {
    return (
        <div className={cx("card-extra", className)} {...rest}>
            {children}
        </div>
    );
}

export function CardBody({ className, children, ...rest }) {
    return (
        <div className={cx("card-body", className)} {...rest}>
            {children}
        </div>
    );
}

/**
 * Body with no padding — for a card whose content is a full-bleed table or list.
 * Equivalent to the markup's `card-body--flush`.
 */
export function CardFlush({ className, children, ...rest }) {
    return (
        <div className={cx("flex-1", className)} {...rest}>
            {children}
        </div>
    );
}

/** Footer strip, used for pagination rows under a table. */
export function CardFooter({ className, children, ...rest }) {
    return (
        <div
            className={cx(
                "flex flex-wrap items-center gap-3 border-t border-slate-200 px-4.5 py-3 dark:border-slate-800",
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
}
